/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';

import { useTranslation } from 'react-i18next';
import Select from '@material-ui/core/Select';
import InputBase from '@material-ui/core/InputBase';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import InputLabel from '@material-ui/core/InputLabel';
import makeStyles from '@material-ui/styles/makeStyles';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import FirstPage from '@material-ui/icons/FirstPage';
import ChevronRight from '@material-ui/icons/ChevronRight';
import LastPage from '@material-ui/icons/LastPage';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import clsx from 'clsx';
import { usePagination } from './usePagination';
import Button from '../../../common/Button';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  container: {
    display: 'flex',
    justifyContent: ({ justify }) => justify,
    marginTop: ({ justify }) => justify === 'center' && '1rem',
    alignItems: 'center',
    maxHeight: '2.75rem',
    margin: '.25rem 0',
    alignSelf: 'center',
  },
  select: {
    margin: '0 .2rem',
    border: `1px solid ${theme.palette?.input}`,
    borderRadius: '.2rem',
    height: '2.25rem',
    '& div': {
      display: 'flex',
      height: '1.337rem',
      padding: '.4rem',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '2px',
    },
    '& svg': {
      color: theme.palette.black,
    },
    '& > div': {
      '&:after': {
        content: '""',
        position: 'absolute',
        top: '-0.313rem',
        right: '-0.313rem',
        bottom: '-0.313rem',
        left: '-0.313rem',
      },
    },
  },
  paginationButton: {
    backgroundColor: theme.palette?.white,
    color: theme.palette.expandIcon,
    margin: '0 .2rem',
    minWidth: '2.25rem',
    width: '2.25rem',
    height: '2.25rem',
    '& span > svg': {
      color: theme.palette?.black,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette?.white,
      '& span > svg': {
        color: theme.palette?.paginationIconDisabled,
      },
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      left: '-0.313rem',
      top: '-0.313rem',
      width: '2.75rem',
      height: '2.75rem',
    },
  },
  border: {
    border: `1px solid ${theme.palette?.input}`,
  },
  noBorder: {
    border: `1px solid ${theme.palette?.paginationButton}`,
  },
  selectLabel: { margin: '.1rem' },
  expandIcon: {
    color: theme.palette.expandIcon,
  },
}));

export default function Pagination({ pageSize, totalCount, page, setPage, justify }) {
  const {
    numberOfPages,
    open,
    onClickNavigation,
    handleOpen,
    handleClose,
    onChangeElementsDisplayed,
    isDisabled,
  } = usePagination(pageSize, totalCount, page, setPage);
  const classes = useStyles({ justify });
  const { t } = useTranslation();

  return (
    <div className={classes.container}>
      <Button
        color="secondary"
        aria-label={t('pagination.go.first.page')}
        disabled={isDisabled('first')}
        className={clsx(classes.paginationButton, isDisabled('first') ? classes.noBorder : classes.border)}
        onClick={onClickNavigation('first')}
      >
        <FirstPage />
      </Button>
      <Button
        color="secondary"
        aria-label={t('pagination.go.previous.page')}
        disabled={isDisabled('back')}
        className={clsx(classes.paginationButton, isDisabled('back') ? classes.noBorder : classes.border)}
        onClick={onClickNavigation('back')}
      >
        <ChevronLeft />
      </Button>
      <InputLabel className={classes.visuallyHidden} id="currentPage">
        {t('pagination.current.page')}
      </InputLabel>

      <Select
        labelId="currentPage"
        variant="outlined"
        className={classes.select}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        IconComponent={() =>
          open ? (
            <ExpandLess className={clsx(classes.expandIcon, 'MuiSelect-icon MuiSelect-iconOutlined')} />
          ) : (
            <ExpandMore
              className={clsx(
                classes.expandIcon,
                `MuiSelect-icon MuiSelect-iconOutlined ${numberOfPages <= 1 ? 'Mui-disabled' : ''}`,
              )}
            />
          )
        }
        value={page + 1}
        onChange={onChangeElementsDisplayed}
        input={<InputBase />}
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          style: { zIndex: 1302 },
        }}
        disabled={numberOfPages <= 1}
      >
        {typeof numberOfPages === 'number' &&
          [...Array(numberOfPages)].map((element, index) => (
            <MenuItem key={index + 1} value={index + 1}>
              {index + 1}
            </MenuItem>
          ))}
      </Select>
      <Typography className={classes.selectLabel} as="span">
        {t('pagination.of.elements', { elements: numberOfPages })}
      </Typography>
      <Button
        color="secondary"
        aria-label={t('pagination.go.next.page')}
        disabled={isDisabled('next')}
        className={clsx(classes.paginationButton, isDisabled('next') ? classes.noBorder : classes.border)}
        onClick={onClickNavigation('next')}
      >
        <ChevronRight />
      </Button>
      <Button
        color="secondary"
        aria-label={t('pagination.go.last.page')}
        disabled={isDisabled('last')}
        className={clsx(classes.paginationButton, isDisabled('last') ? classes.noBorder : classes.border)}
        onClick={onClickNavigation('last')}
      >
        <LastPage />
      </Button>
    </div>
  );
}
